import { FC, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaginationBar from '../../components/PaginationBar';
import { VehicleBrand } from '../../api/vehicleBrands';
import {
  VehicleModel,
  createVehicleModel,
  deleteVehicleModel,
  fetchVehicleModels,
  updateVehicleModel,
} from '../../api/vehicleModels';
import SelectVehicleBrand from '../../components/SelectVehicleBrand';

interface ModelsState {
  records: VehicleModel[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const formatDate = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
};

const VehicleModelsPanel: FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [filterBrandId, setFilterBrandId] = useState<'all' | string>('all');
  const [state, setState] = useState<ModelsState>({
    records: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [brandOptions, setBrandOptions] = useState<VehicleBrand[]>([]);
  const [brandOptionsLoading, setBrandOptionsLoading] = useState(false);
  const [brandOptionsError, setBrandOptionsError] = useState<string | null>(null);

  const [createBrandId, setCreateBrandId] = useState('');
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBrandId, setEditBrandId] = useState('');
  const [editName, setEditName] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [brandRefreshKey, setBrandRefreshKey] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const triggerRefresh = () => setRefreshCounter((prev) => prev + 1);

  const reconcileBrandSelection = useCallback((brands: VehicleBrand[]) => {
    setCreateBrandId((prev) => {
      if (prev && brands.some((brand) => brand.id === prev)) {
        return prev;
      }
      if (brands.length === 1) {
        return brands[0].id;
      }
      return '';
    });

    setEditBrandId((prev) => {
      if (prev && brands.some((brand) => brand.id === prev)) {
        return prev;
      }
      return '';
    });
  }, []);

  const loadBrandOptions = () => {
    setBrandOptionsLoading(true);
    setBrandOptionsError(null);
    setBrandOptions([]);
    setBrandRefreshKey((prev) => prev + 1);
  };

  const handleBrandStatusChange = useCallback(
    ({ loading: nextLoading, error: nextError, brands }: { loading: boolean; error: string | null; brands: VehicleBrand[] }) => {
      setBrandOptionsLoading(nextLoading);
      setBrandOptionsError(nextError);
      if (!nextLoading && !nextError) {
        setBrandOptions(brands);
        reconcileBrandSelection(brands);
      }
      if (!nextLoading && nextError) {
        setBrandOptions([]);
      }
    },
    [reconcileBrandSelection],
  );

  const handleBrandOptionsLoaded = useCallback(
    (brands: VehicleBrand[]) => {
      setBrandOptions(brands);
      reconcileBrandSelection(brands);
    },
    [reconcileBrandSelection],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setListError(null);

    const brandId = filterBrandId === 'all' ? undefined : filterBrandId;

    fetchVehicleModels({
      page,
      limit: state.limit,
      brandId,
      search: debouncedSearch || undefined,
    })
      .then((response) => {
        if (!isMounted) {
          return;
        }

        const {
          data,
          meta: { total, page: currentPage, limit, totalPages },
        } = response;

        setState({
          records: data,
          total,
          page: currentPage,
          limit,
          totalPages,
        });
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to load vehicle models.';
        setListError(message);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [filterBrandId, page, refreshCounter, state.limit, debouncedSearch]);

  useEffect(() => {
    if (editingId) {
      const editingModel = state.records.find((model) => model.id === editingId);
      if (!editingModel) {
        setEditingId(null);
        setEditBrandId('');
        setEditName('');
        setEditError(null);
      }
    }
  }, [editingId, state.records]);

  const brandLookup = useMemo(() => {
    const map = new Map<string, string>();
    brandOptions.forEach((brand) => {
      map.set(brand.id, brand.name);
    });
    return map;
  }, [brandOptions]);

  const totalPages = useMemo(() => Math.max(1, state.totalPages || 1), [state.totalPages]);

  const handleCreateSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (creating) {
      return;
    }

    const brandId = createBrandId || (brandOptions.length === 1 ? brandOptions[0].id : '');

    if (!brandId) {
      setCreateError('Select a brand before adding a model.');
      return;
    }

    const trimmedName = createName.trim();
    if (!trimmedName) {
      setCreateError('Enter a model name.');
      return;
    }

    setCreateError(null);
    setCreating(true);

    try {
      await createVehicleModel({ brandId, name: trimmedName });
      setCreateName('');
      setCreateBrandId(brandId);
      setFilterBrandId(brandId);
      setPage(1);
      triggerRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create vehicle model.';
      setCreateError(message);
    } finally {
      setCreating(false);
    }
  };

  const startEditing = (model: VehicleModel) => {
    setEditingId(model.id);
    setEditBrandId(model.brandId);
    setEditName(model.name);
    setEditError(null);
    setActionError(null);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditBrandId('');
    setEditName('');
    setEditError(null);
    setUpdatingId(null);
  };

  const handleEditSave = async () => {
    if (!editingId || updatingId === editingId) {
      return;
    }

    const brandId = editBrandId;
    const name = editName.trim();

    if (!brandId) {
      setEditError('Select a brand for this model.');
      return;
    }

    if (!name) {
      setEditError('Enter a model name.');
      return;
    }

    setEditError(null);
    setUpdatingId(editingId);

    try {
      await updateVehicleModel(editingId, { brandId, name });
      cancelEditing();
      triggerRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update vehicle model.';
      setEditError(message);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (model: VehicleModel) => {
    if (deletingId || !window.confirm(`Delete model "${model.name}"?`)) {
      return;
    }

    setDeletingId(model.id);
    setActionError(null);

    try {
      await deleteVehicleModel(model.id);
      if (state.records.length <= 1 && state.page > 1) {
        setPage((prev) => Math.max(1, prev - 1));
      } else {
        triggerRefresh();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete vehicle model.';
      setActionError(message);
    } finally {
      setDeletingId(null);
    }
  };

  const summaryLabel = useMemo(() => {
    if (!state.total) {
      return 'No vehicle models found';
    }

    const start = (state.page - 1) * state.limit;
    const end = start + state.records.length;
    return `Showing ${end} of ${state.total} vehicle models`;
  }, [state.limit, state.page, state.records.length, state.total]);

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search models..."
            className="w-full sm:w-80 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div className="flex flex-col justify-start gap-1">
            <label className="text-sm font-medium text-slate-600">Filter by brand</label>
            <SelectVehicleBrand
              id="vehicle-model-brand-filter"
              value={filterBrandId === 'all' ? 'all' : filterBrandId}
              onChange={(brandId) => {
                const next = brandId || 'all';
                setFilterBrandId(next as 'all' | string);
                setPage(1);
              }}
              selectClassName="min-w-[20rem] appearance-none rounded-xl border border-slate-200 bg-white px-3 pr-10 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              helperClassName="text-xs"
              placeholderLabel="All brands"
              placeholderValue="all"
              onStatusChange={handleBrandStatusChange}
              onOptionsLoaded={handleBrandOptionsLoaded}
              refreshKey={brandRefreshKey}
            />
          </div>
          {filterBrandId !== 'all' && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary shadow-sm"
              onClick={() => {
                setSearchTerm('');
                setDebouncedSearch('');
                setFilterBrandId('all');
                setPage(1);
              }}
            >
              <span>✕</span> Clear Filter
            </button>
          )}
        </div>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          onClick={loadBrandOptions}
          disabled={brandOptionsLoading}
        >
          {brandOptionsLoading ? 'Refreshing…' : 'Refresh brands'}
        </button>
      </div>
      {
        brandOptionsError ? (
          <p className="mt-2 text-sm text-danger">{brandOptionsError}</p>
        ) : null
      }

      <form
        className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 p-4 shadow-sm"
        onSubmit={handleCreateSubmit}
      >

        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
          <SelectVehicleBrand
            value={createBrandId}
            onChange={(brandId) => setCreateBrandId(brandId)}
            className="w-full sm:w-72"
            selectClassName="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            helperClassName="text-xs"
            placeholderLabel="Select brand…"
            refreshKey={brandRefreshKey}
            disabled={creating || brandOptions.length === 0}
          />
          <input
            type="text"
            value={createName}
            onChange={(event) => setCreateName(event.target.value)}
            placeholder="e.g. Corolla"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-64"
            disabled={creating}
          />
          <button
            type="submit"
            className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={creating || brandOptions.length === 0}
          >
            {creating ? 'Saving…' : 'Add'}
          </button>
        </div>
        {createError ? (
          <p className="mt-2 text-sm text-danger">{createError}</p>
        ) : null}
        {brandOptions.length === 0 && !brandOptionsLoading && !brandOptionsError ? (
          <p className="mt-2 text-sm text-slate-500">
            Add a vehicle brand before creating models.
          </p>
        ) : null}
      </form>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Brand</th>
              <th className="px-5 py-3 font-semibold">Model</th>
              <th className="px-5 py-3 font-semibold">Related products</th>
              <th className="px-5 py-3 font-semibold">Updated</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={5}>
                  Loading...
                </td>
              </tr>
            ) : listError ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={5}>
                  Unable to load models. Please try again.
                </td>
              </tr>
            ) : state.records.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={5}>
                  No models found.
                </td>
              </tr>
            ) : (
              state.records.map((model) => {
                const isEditing = model.id === editingId;
                const brandName = model.brand?.name ?? brandLookup.get(model.brandId) ?? '—';
                const relatedCount = model.relatedVariantCount ?? 0;

                return (
                  <tr key={model.id} className="even:bg-slate-50/60">
                    <td className="px-5 py-3 text-slate-700">
                      {isEditing ? (
                        <SelectVehicleBrand
                          value={editBrandId}
                          onChange={(brandId) => setEditBrandId(brandId)}
                          className="w-full"
                          selectClassName="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                          helperClassName="text-xs"
                          placeholderLabel="Select brand…"
                          refreshKey={brandRefreshKey}
                          disabled={updatingId === model.id || brandOptions.length === 0}
                          showStatus={false}
                        />
                      ) : (
                        brandName
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-700">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={(event) => setEditName(event.target.value)}
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                          disabled={updatingId === model.id}
                        />
                      ) : (
                        model.name
                      )}
                      {isEditing && editError ? (
                        <p className="mt-1 text-xs text-danger">{editError}</p>
                      ) : null}
                    </td>
                    <td className="px-5 py-3 text-slate-500">{relatedCount}</td>
                    <td className="px-5 py-3 text-slate-500">{formatDate(model.updatedAt)}</td>
                    <td className="px-5 py-3 text-right">
                      {isEditing ? (
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
                            onClick={handleEditSave}
                            disabled={updatingId === model.id}
                          >
                            {updatingId === model.id ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-70"
                            onClick={cancelEditing}
                            disabled={updatingId === model.id}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                            onClick={() => startEditing(model)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                            onClick={() => navigate(`/vehicle-management/models/${model.id}/related-products`)}
                          >
                            Related products
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg border border-danger/40 px-3 py-1.5 text-xs font-semibold text-danger transition hover:bg-danger/10 disabled:cursor-not-allowed disabled:opacity-60"
                            onClick={() => handleDelete(model)}
                            disabled={deletingId === model.id}
                          >
                            {deletingId === model.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={state.page}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={<span>{summaryLabel}</span>}
        renderExtra={
          <span className="text-xs text-slate-400">Page {state.page} of {totalPages} ({state.limit} / page)</span>
        }
      />

      {
        actionError ? (
          <p className="mt-4 text-sm text-danger">{actionError}</p>
        ) : null
      }
    </div >
  );
};

export default VehicleModelsPanel;
